/**
 * Match setup: build a hex World, generate terrain + resources, register the
 * player roster, pick spread-out start hexes, and spawn each player's starting
 * entities (a town center + a few workers of that player's civ).
 *
 * The roster can come from the map JSON (default) OR from an explicit
 * `opts.players` list (the New Game screen) — so you can field any mix of
 * human/bot players and civilizations. Positions are derived from the hex grid.
 */
import { World } from "./world";
import type { Registry } from "../data/registry";
import type { MapDef, StartingEntity } from "../data/schema";
import type { PlayerState, Vec2 } from "./types";
import {
  generateHexGrid,
  applyTerrainRecipe,
  populateWorld,
  findStarts,
  type HexGenOptions,
  type TerrainRecipeOptions,
} from "./mapgen";
import { spawnBuilding, spawnResourceNode, spawnUnit } from "./spawn";
import { attachWorldHelpers } from "./commands";

/** One configured player for a match (from the New Game screen). */
export interface PlayerConfig {
  civ: string;
  kind: "human" | "bot";
  /** Alliance team (players sharing a team are allies). Default = slot index. */
  team?: number;
}

export type MatchOptions = HexGenOptions & {
  /** Explicit roster (player 0 first). Overrides the map's fixed players. */
  players?: PlayerConfig[];
};

/** Distinct per-slot ownership colours (independent of civ colour). */
const PLAYER_COLORS = [
  "#d34d4d", "#3b82d9", "#46a758", "#d9a441",
  "#9b59b6", "#16a0a0", "#e07b39", "#c2407a",
];

const STARTING_WORKERS = 4;
const terrainRecipes = new WeakMap<World, TerrainRecipeOptions>();

export function terrainRecipeFor(world: World): TerrainRecipeOptions {
  return terrainRecipes.get(world) ?? {};
}

export function setTerrainRecipe(world: World, recipe: TerrainRecipeOptions): void {
  terrainRecipes.set(world, {
    base: recipe.base,
    features: recipe.features?.map((feature) => ({ ...feature })),
    protectedHexes: recipe.protectedHexes?.map((hex) => ({ ...hex })),
  });
}

/**
 * Generate the terrain grid for a match. The SINGLE source of gen parameters so
 * a saved game (which stores only seed + dims) regenerates byte-identical
 * terrain on restore. mountainLift carves steep, unclimbable flanks onto the
 * highlands.
 */
export function matchHexes(cols: number, rows: number, size: number, seed: number, recipe: TerrainRecipeOptions = {}) {
  const hexes = generateHexGrid({ cols, rows, size, seed, mountainLift: 2 });
  applyTerrainRecipe(hexes, recipe, seed, size);
  return hexes;
}

export function createMatch(
  reg: Registry,
  mapId: string,
  opts?: MatchOptions
): World {
  const map: MapDef = reg.map(mapId);
  const cols = opts?.cols ?? map.width;
  const rows = opts?.rows ?? map.height;
  // match the 3D demo's world extent (~96 units wide) so hex SIZE shrinks
  // with finer resolution instead of the map growing.
  const size = opts?.size ?? 96 / (cols * 1.5);
  const seed = opts?.seed ?? (Number(map.terrain.seed) || 1337);
  const generatedStarts = !!opts?.players?.length;
  const authoredPositions = map.startingEntities.flatMap((entity) =>
    entity.position && entity.position[0] >= 0 && entity.position[0] < cols && entity.position[1] >= 0 && entity.position[1] < rows
      ? [{ col: entity.position[0], row: entity.position[1] }]
      : [],
  );

  const terrain: TerrainRecipeOptions = {
    base: map.terrain.base,
    features: map.terrain.features,
    protectedHexes: generatedStarts ? undefined : authoredPositions,
  };
  const hexes = matchHexes(cols, rows, size, seed, terrain);
  const world = new World(cols, rows, size, hexes, seed);
  setTerrainRecipe(world, terrain);

  // Roster: an explicit config (New Game) overrides the map's fixed players.
  // Default team = slot index (free-for-all), unless the config/map sets one.
  const roster = opts?.players && opts.players.length
    ? opts.players.map((p, i) => ({ id: i, civ: p.civ, color: PLAYER_COLORS[i % PLAYER_COLORS.length], kind: p.kind, team: p.team ?? i }))
    : map.players.map((p) => ({ id: p.id, civ: p.civ, color: p.color, kind: p.kind, team: p.team ?? p.id }));

  for (const p of roster) {
    const ps: PlayerState = {
      id: p.id,
      civ: p.civ,
      color: p.color,
      kind: p.kind,
      team: p.team,
      resources: { ...map.startingResources },
      population: 0,
      populationCap: 5, // base cap; houses raise this
      alive: true,
      defeated: false,
    };
    world.players.set(p.id, ps);
  }

  // Explicit rosters are a generated New Game; map rosters use authored entities.
  const starts = generatedStarts ? findStarts(world, roster.length) : [];

  // exclude each start hex + its immediate neighbors (TC + worker ring)
  const exclude = new Set<number>();
  const authoredStarts = map.players.flatMap((player) => {
    const buildings = map.startingEntities.filter((entity) =>
      entity.player === player.id && entity.kind === "building" && entity.position &&
      world.inBounds(entity.position[0], entity.position[1]),
    );
    const core = buildings.find((entity) => reg.building(player.civ, entity.type).isCore);
    const position = core?.position ?? player.position ?? buildings[0]?.position;
    return position && world.inBounds(position[0], position[1])
      ? [{ col: position[0], row: position[1] }]
      : [];
  });
  for (const s of [...starts, ...authoredPositions]) {
    exclude.add(world.hkey(s.col, s.row));
    for (const [nc, nr] of world.neighbors(s.col, s.row)) exclude.add(world.hkey(nc, nr));
  }

  const hasAuthoredResources =
    map.terrain.startingResources !== undefined ||
    map.terrain.scatteredResources !== undefined;
  populateWorld(world, reg, exclude, !hasAuthoredResources);

  if (generatedStarts) {
    // spawn each generated start: a town center + workers spiralling out
    for (let i = 0; i < roster.length; i++) {
      const p = roster[i];
      const start = starts[i];
      if (!start) continue;
      const ps = world.players.get(p.id);
      if (!ps) continue;

      const used = new Set<number>([world.hkey(start.col, start.row)]);
      spawnBuilding(world, reg, ps.civ, "town_center", p.id, { x: start.col, y: start.row }, false);

      const worker = reg.roleUnit(ps.civ, "worker");
      if (worker) {
        for (let k = 0; k < STARTING_WORKERS; k++) {
          const spot = findFreeHexNear(world, start, used);
          const pos: Vec2 = spot
            ? world.hexCenter(spot.col, spot.row)
            : world.hexCenter(start.col, start.row);
          if (spot) used.add(world.hkey(spot.col, spot.row));
          spawnUnit(world, reg, ps.civ, worker.id, p.id, pos);
        }
      }
    }
  } else {
    spawnAuthoredEntities(world, reg, map.startingEntities);
  }

  if (hasAuthoredResources) {
    spawnTerrainResources(world, reg, map, generatedStarts ? starts : authoredStarts, exclude);
  }

  attachWorldHelpers(world); // gather/production helpers, before the first step
  return world;
}

function spawnAuthoredEntities(world: World, reg: Registry, entities: StartingEntity[]): void {
  for (const entity of entities) {
    if (!entity.position || !world.inBounds(entity.position[0], entity.position[1])) continue;
    const player = world.players.get(entity.player);
    if (!player) continue;
    if (entity.kind === "building") {
      spawnBuilding(world, reg, player.civ, entity.type, player.id, { x: entity.position[0], y: entity.position[1] }, false);
    } else {
      const unit = reg.roleUnit(player.civ, entity.type);
      if (unit) spawnUnit(world, reg, player.civ, unit.id, player.id, world.hexCenter(entity.position[0], entity.position[1]));
    }
  }
}

/** Apply the map-authored resource recipe after procedural terrain is available. */
function spawnTerrainResources(
  world: World,
  reg: Registry,
  map: MapDef,
  starts: { col: number; row: number }[],
  exclude: Set<number>,
): void {
  for (const start of starts) {
    for (const recipe of map.terrain.startingResources ?? []) {
      for (let i = 0; i < recipe.count; i++) {
        const spot = findFreeHexNear(world, start, exclude, recipe.distance);
        if (spot) spawnResourceNode(world, reg, recipe.type, { x: spot.col, y: spot.row });
      }
    }
  }
  for (const recipe of map.terrain.scatteredResources ?? []) {
    for (let cluster = 0; cluster < recipe.count; cluster++) {
      const spots = findResourceCluster(world, recipe.perCluster, exclude);
      for (const spot of spots) {
        spawnResourceNode(world, reg, recipe.type, { x: spot.col, y: spot.row });
      }
    }
  }
}

/** Choose one random valid seed, then fill adjacent valid hexes as a cluster. */
function findResourceCluster(
  world: World,
  count: number,
  exclude: Set<number>,
): { col: number; row: number }[] {
  const candidates = world.hexes.filter(
    (hex) => world.isWalkable(hex.col, hex.row) && !exclude.has(world.hkey(hex.col, hex.row)),
  );
  if (!candidates.length || count <= 0) return [];

  // Try deterministic random seeds until one has enough connected free space.
  // Keep the largest partial cluster as a graceful fallback on crowded maps.
  let best: { col: number; row: number }[] = [];
  const first = Math.floor(world.rng.next() * candidates.length);
  for (let attempt = 0; attempt < Math.min(candidates.length, 64); attempt++) {
    const start = candidates[(first + attempt) % candidates.length];
    const seen = new Set<number>();
    const queue: { col: number; row: number }[] = [{ col: start.col, row: start.row }];
    const spots: { col: number; row: number }[] = [];
    seen.add(world.hkey(start.col, start.row));

    let cursor = 0;
    while (cursor < queue.length && spots.length < count) {
      const spot = queue[cursor++];
      if (!world.isWalkable(spot.col, spot.row) || exclude.has(world.hkey(spot.col, spot.row))) continue;
      spots.push(spot);
      for (const [col, row] of world.neighbors(spot.col, spot.row)) {
        const key = world.hkey(col, row);
        if (seen.has(key)) continue;
        seen.add(key);
        if (world.isWalkable(col, row) && !exclude.has(key)) queue.push({ col, row });
      }
    }

    if (spots.length === count) return spots;
    if (spots.length > best.length) best = spots;
  }
  return best;
}

/** BFS outward from `start`; first walkable hex not already in `used`. */
function findFreeHexNear(
  world: World,
  start: { col: number; row: number },
  used: Set<number>,
  minDistance = 0
): { col: number; row: number } | null {
  const startKey = world.hkey(start.col, start.row);
  const seen = new Set<number>([startKey]);
  const queue: { col: number; row: number; distance: number }[] = [
    { col: start.col, row: start.row, distance: 0 },
  ];
  let cursor = 0;
  while (cursor < queue.length) {
    const current = queue[cursor++];
    if (
      current.distance >= minDistance &&
      world.isWalkable(current.col, current.row) &&
      !used.has(world.hkey(current.col, current.row))
    ) {
      return { col: current.col, row: current.row };
    }
    for (const [col, row] of world.neighbors(current.col, current.row)) {
      const key = world.hkey(col, row);
      if (seen.has(key)) continue;
      seen.add(key);
      queue.push({ col, row, distance: current.distance + 1 });
    }
  }
  return null;
}
